import React, { useEffect } from 'react';
import { Formik, ErrorMessage } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Spinner } from 'components';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import usePayment from 'hooks/usePayment';
import { SET_ERROR_MESSAGE } from 'helpers/types';
import { AddCardFormContent } from 'services/content';
import styles from './style.module.css';
import * as Types from 'helpers/types';
import useQuery from 'hooks/useQuery';
import { CLIENT_SIDE_ERROR_ADD_CARD } from 'helpers/monitoringEvents';
import useMonitoringEvents from 'hooks/useMonitoringEvents';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();

const AddNewCardSchema = Yup.object().shape({
  card_expiry_year: Yup.string()
    .test('valid-year', 'Please select a year', (value) => value !== 'YYYY')
    .required('Expiry year is required'),
  card_expiry_month: Yup.string()
    .test('valid-month', 'Please select a month', (value) => value !== 'MM')
    .when('card_expiry_year', {
      is: (card_expiry_year) => {
        if (card_expiry_year === currentYear.toString()) return true;
        return false;
      },
      then: Yup.string().test(
        'valid-month',
        'Month has to be in the future',
        (value) => parseInt(value - 1) >= currentMonth
      ),
    })
    .required('Expiry month is required'),
  card_name: Yup.string(),
});

const AddNewCardForm = ({ redirect }) => {
  let query = useQuery();
  const history = useHistory();
  const session = useSelector((state) => state.payment.session.id);
  const [, , , , , , postTokenizeCard] = usePayment();
  const dispatch = useDispatch();
  const [showCVVInfo, setCVVInfo] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [cardNumberError, setCardNumberError] = React.useState('');
  const [cardMonthError, setCardMonthError] = React.useState('');
  const [cardYearError, setCardYearError] = React.useState('');
  const [CVVError, setCVVError] = React.useState('');
  let script;
  const accountView = query.get('view') === 'account' ? true : false;
  const [postLog] = useMonitoringEvents();

  const initialValues = {
    card_number: '',
    card_expiry_year: 'YYYY',
    card_expiry_month: 'MM',
    card_name: '',
    card_cvv: '',
  };

  useEffect(() => {
    if (!session) {
      dispatch({
        type: SET_ERROR_MESSAGE,
        payload: {
          msg: 'Creating new payment failed, please try again',
          newMessage: true,
          link: '/pharmacy/dashboard/payment',
          ctaText: 'Back to payment options',
        },
      });
      history.push('/pharmacy/error');
    }
    script = document.createElement('script');
    script.src = `https://eu-gateway.mastercard.com/form/version/49/merchant/${window.MERCHANT_ID}/session.js?debug=true`;
    script.async = true;
    document.body.appendChild(script);

    function configureMasterCard() {
      script.removeEventListener('load', configureMasterCard);
      window.PaymentSession.configure({
        session,
        fields: {
          card: {
            number: '#card_number',
            securityCode: '#card_cvv',
            expiryMonth: '#card_expiry_month',
            expiryYear: '#card_expiry_year',
            nameOnCard: '#card_name',
          },
        },
        frameEmbeddingMitigation: ['javascript', 'x-frame-options', 'csp'],
        callbacks: {
          initialized: function (response) {
            setIsLoading(false);
            if (response.status === 'system_error') {
              history.push('/pharmacy/error');
            }
          },
          formSessionUpdate: async function (response) {
            if (response.status) {
              if ('ok' === response.status) {
                try {
                  const newCardDetails = await postTokenizeCard(session);
                  dispatch({ type: Types.PAYMENT_SET, payload: newCardDetails });
                  accountView ? history.goBack() : history.push('/pharmacy/dashboard/payment');
                } catch (e) {
                  history.push('/pharmacy/error');
                }
              } else if ('fields_in_error' === response.status) {
                if (response.errors.cardNumber) {
                  setCardNumberError('Card number invalid or missing.');
                }
                if (response.errors.expiryYear) {
                  setCardYearError('Expiry year invalid or missing.');
                }
                if (response.errors.expiryMonth) {
                  setCardMonthError('Expiry year invalid or missing.');
                }
                if (response.errors.securityCode) {
                  setCVVError('Security code invalid.');
                }
              } else if (
                'payment_type_required' === response.status ||
                'giftCard_type_required' === response.status ||
                'request_timeout' === response.status ||
                'system_error' === response.status
              ) {
                history.push('/pharmacy/error');
              }
            } else {
              history.push('/pharmacy/error');
            }
          },
        },
      });
    }

    script.addEventListener('load', configureMasterCard);

    return function cleanupListener() {
      window.removeEventListener('load', configureMasterCard);
    };
  }, []);

  const submitForm = async (payload, actions) => {
    try {
      await window.PaymentSession.updateSessionFromForm('card');
    } catch (e) {
      postLog(CLIENT_SIDE_ERROR_ADD_CARD);
      history.push('/pharmacy/error');
    }
  };

  const displayCVVInfo = () => {
    return (
      <div>
        <button type="button" className={styles.link_button} href="#" onClick={() => setCVVInfo(!showCVVInfo)}>
          {AddCardFormContent.displayCVVLinkText}
        </button>
        {showCVVInfo ? (
          <div className={styles.info_container}>
            <h2 className={styles.info_header}>{AddCardFormContent.displayCVVTitleText}</h2>
            <p className={styles.info_intro_text}>{AddCardFormContent.displayCVVIntroText}</p>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {isLoading && <Spinner />}
      <div className={isLoading ? styles.invisible : null}>
        <Formik
          initialValues={initialValues}
          validationSchema={AddNewCardSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values, actions) => submitForm(values, actions)}>
          {(props) => (
            <form className={styles.form} onSubmit={props.handleSubmit}>
              <div className={styles.inputContainer}>
                <label htmlFor="card_number">
                  <h2 className={styles.inputTitles}>{AddCardFormContent.cardNumberTitleText}</h2>
                  {cardNumberError && <div className={`coop-c-form-error__text`}>{cardNumberError}</div>}
                  <input
                    maxLength="255"
                    id="card_number"
                    type="text"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.card_number}
                    readOnly
                  />
                </label>
                <h2 className={styles.inputTitles}>{AddCardFormContent.cardExpiryTitleText}</h2>
                <p className={styles.inputSubTitles}>{AddCardFormContent.cardExpiryTextPlaceholder}</p>
                <div className={`${styles.cardDateErrors} coop-c-form-error__text`}>
                  {cardMonthError ? (
                    <>{`${cardMonthError} `}</>
                  ) : (
                    <ErrorMessage name="card_expiry_month" render={(msg) => `${msg} `} />
                  )}
                  {cardYearError ? (
                    <>{cardYearError}</>
                  ) : (
                    <ErrorMessage name="card_expiry_year" render={(msg) => msg} />
                  )}
                </div>
                <div className={styles.expiryContainer}>
                  <div className={styles.expirySelect}>
                    <label htmlFor="card_expiry_month">
                      <select
                        id="card_expiry_month"
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        value={props.values.card_expiry_month}>
                        <option value="MM">MM</option>
                        {[...Array(12)].map((e, month) => (
                          <option key={month + 1} value={month + 1}>
                            {month + 1}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className={styles.expirySelect}>
                    <label htmlFor="card_expiry_year">
                      <select
                        id="card_expiry_year"
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        value={props.values.card_expiry_year}>
                        <option value="YYYY">YYYY</option>
                        {[...Array(50)].map((e, year) => (
                          <option key={currentYear + year} value={currentYear + year}>
                            {currentYear + year}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                <label htmlFor="card_name">
                  <h2 className={styles.inputTitles}>{AddCardFormContent.cardNameTitleText}</h2>
                  <input
                    maxLength="255"
                    id="card_name"
                    type="text"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.card_name}
                  />
                </label>
                <label htmlFor="card_cvv">
                  <h2 className={styles.inputTitles}>{AddCardFormContent.cardCVVTitleText}</h2>
                  {CVVError && <div className={`coop-c-form-error__text`}>{CVVError}</div>}
                  <input
                    maxLength="255"
                    id="card_cvv"
                    type="text"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.card_cvv}
                    readOnly
                  />
                </label>
                {displayCVVInfo()}
              </div>
              <button type="submit" className={`coop-btn coop-btn--primary coop-c-hero__cta ${styles.submit}`}>
                {AddCardFormContent.saveCardText}
              </button>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

AddNewCardForm.propTypes = {
  redirect: PropTypes.string.isRequired,
};

AddNewCardForm.defaultProps = {
  redirect: '/pharmacy/dashboard/account/address',
};

export default AddNewCardForm;
